// PatientContext — the heart of the multi-patient identity model.
// See docs/PATIENT_PORTAL_PLAN.md §3.
//
// One authenticated User can map to many PatientProfiles (family, multi-brand,
// caregiver). All patient-scoped UI reads `currentProfile` from here and all
// patient-scoped fetches flow through services that read this context.
//
// Switching profile MUST invalidate every cached patient-scoped query —
// react-query keys for patient resources should always include
// `currentProfile.id` so this is automatic. The visible profile-name/avatar
// change in the top bar is the user-facing safety signal that the chart
// they're looking at belongs to the right person.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  archiveMyProfile,
  createMyProfile,
  listMyProfiles,
  updateMyProfile,
} from '../services/profilesService'

const LAST_PROFILE_KEY = 'patientPortal.currentProfileId'

const PatientContext = createContext(null)

export function usePatient() {
  const ctx = useContext(PatientContext)
  if (!ctx) {
    throw new Error('usePatient must be used inside <PatientProvider>')
  }
  return ctx
}

function readLastProfileId() {
  try {
    return localStorage.getItem(LAST_PROFILE_KEY) || null
  } catch (e) {
    return null
  }
}

function writeLastProfileId(id) {
  try {
    if (id) localStorage.setItem(LAST_PROFILE_KEY, id)
    else localStorage.removeItem(LAST_PROFILE_KEY)
  } catch (e) {
    // ignore
  }
}

export function PatientProvider({ children }) {
  const [profiles, setProfiles] = useState([])
  const [currentProfileId, setCurrentProfileId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refreshProfiles = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listMyProfiles()
      setProfiles(list)
      const last = readLastProfileId()
      if (last && list.some((p) => p.id === last)) {
        setCurrentProfileId(last)
      } else if (list.length === 1) {
        setCurrentProfileId(list[0].id)
        writeLastProfileId(list[0].id)
      } else {
        // 0 profiles → onboarding; >1 with no last → picker
        setCurrentProfileId(null)
      }
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshProfiles()
  }, [refreshProfiles])

  const switchProfile = useCallback((profileId) => {
    setCurrentProfileId(profileId)
    writeLastProfileId(profileId)
    // Patient-scoped query caches are keyed off currentProfile.id so
    // consumers naturally re-fetch. If a global cache invalidation hook
    // is added later (react-query QueryClient.invalidateQueries) this is
    // where it would fire.
  }, [])

  const addProfile = useCallback(
    async (input) => {
      const created = await createMyProfile(input)
      setProfiles((prev) => [...prev, created])
      switchProfile(created.id)
      return created
    },
    [switchProfile],
  )

  const editProfile = useCallback(async (id, patch) => {
    const updated = await updateMyProfile(id, patch)
    setProfiles((prev) => prev.map((p) => (p.id === id ? updated : p)))
    return updated
  }, [])

  const archiveProfile = useCallback(
    async (id) => {
      await archiveMyProfile(id)
      setProfiles((prev) => prev.filter((p) => p.id !== id))
      if (currentProfileId === id) {
        setCurrentProfileId(null)
        writeLastProfileId(null)
      }
    },
    [currentProfileId],
  )

  const currentProfile = useMemo(
    () => profiles.find((p) => p.id === currentProfileId) || null,
    [profiles, currentProfileId],
  )

  // §3.6 — group profiles for v1 by `relation` (SELF vs others) so the
  // switcher already has the visual structure that will collapse same-
  // PersonIdentity profiles in v2.
  const groupedProfiles = useMemo(() => {
    const self = profiles.filter((p) => p.relation === 'SELF')
    const others = profiles.filter((p) => p.relation !== 'SELF')
    return { self, others }
  }, [profiles])

  const value = useMemo(
    () => ({
      profiles,
      groupedProfiles,
      currentProfile,
      currentProfileId,
      loading,
      error,
      switchProfile,
      addProfile,
      editProfile,
      archiveProfile,
      refreshProfiles,
    }),
    [
      profiles,
      groupedProfiles,
      currentProfile,
      currentProfileId,
      loading,
      error,
      switchProfile,
      addProfile,
      editProfile,
      archiveProfile,
      refreshProfiles,
    ],
  )

  return <PatientContext.Provider value={value}>{children}</PatientContext.Provider>
}

export default PatientContext
